/**
 * AlertImagePicker — drop-in input that lets the user attach up to N images
 * to an alert. Compresses each to a base64 JPEG before lifting them up to
 * the parent via `onChange(images)`.
 *
 * Designed to fit both the CreateAlertDialog (composer) and the post-create
 * "Add photos" sheet from the alert detail. The parent controls the array;
 * we never store images locally past the call to onChange.
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { compressImage } from "../utils/compressImage";

const DEFAULT_MAX = 3;

export default function AlertImagePicker({ images = [], onChange, max = DEFAULT_MAX, disabled = false }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const remaining = Math.max(0, max - images.length);

  const handlePick = () => {
    if (!inputRef.current || disabled || remaining === 0) return;
    inputRef.current.value = ""; // allow re-selecting the same file
    inputRef.current.click();
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, remaining);
    if (files.length === 0) return;
    setBusy(true);
    const next = [...images];
    for (const f of files) {
      try {
        const dataUrl = await compressImage(f);
        next.push(dataUrl);
      } catch (err) {
        toast.error(t("image_picker.file_error", { name: f.name, error: err.message || t("image_picker.could_not_process") }));
      }
    }
    setBusy(false);
    onChange(next);
  };

  const remove = (idx) => {
    if (disabled) return;
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="space-y-1.5" data-testid="alert-image-picker">
      <div className="flex flex-wrap gap-2">
        {images.map((src, idx) => (
          <div
            key={`${idx}-${src.slice(-24)}`}
            className="relative w-20 h-20 rounded-sm overflow-hidden border border-gray-300 bg-gray-50"
            data-testid={`alert-image-preview-${idx}`}
          >
            <img src={src} alt={`Attachment ${idx + 1}`} className="w-full h-full object-cover" />
            {!disabled && (
              <button
                type="button"
                onClick={() => remove(idx)}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center"
                aria-label={t("image_picker.remove_image")}
                data-testid={`alert-image-remove-${idx}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
        {remaining > 0 && !disabled && (
          <button
            type="button"
            onClick={handlePick}
            disabled={busy}
            data-testid="alert-image-add-btn"
            className="w-20 h-20 rounded-sm border-2 border-dashed border-gray-300 hover:border-gray-500 text-gray-500 hover:text-gray-700 flex flex-col items-center justify-center gap-1 text-[10px] font-body bg-white transition-colors disabled:opacity-50"
            title={t("image_picker.add_photo_tooltip", { remaining })}
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
            <span>{busy ? t("image_picker.compressing") : t("image_picker.add_photo")}</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        capture="environment"
        className="hidden"
        onChange={handleFiles}
        data-testid="alert-image-input"
      />
      {images.length > 0 && (
        <p className="text-[10px] text-gray-500 font-body">
          {t("image_picker.counter", { n: images.length, max })}
        </p>
      )}
    </div>
  );
}
